import { useFormContext, Controller } from "react-hook-form";
import { useState, useEffect } from "react";

interface MenuItem {
  MenuID: number;
  Name: string;
  IsSelected: number;
  SubmenuItems: MenuItem[];
}

interface MenuRightsSelectorProps {
  menuData: MenuItem[];
  name: string;
  label: string;
  setDashboardName: (name: string | null) => void;
}

const MenuRightsSelector: React.FC<MenuRightsSelectorProps> = ({
  menuData,
  name,
  label,
  setDashboardName,
}) => {
  const { control, setValue, getValues } = useFormContext();
  const [openMenus, setOpenMenus] = useState<number[]>([]);

  // Initialize form field with MenuIDs where IsSelected === 1 and set dashboardName
  useEffect(() => {
    const initializeSelections = (items: MenuItem[]): number[] => {
      let selections: number[] = [];
      items.forEach((item) => {
        if (item.IsSelected === 1) {
          selections.push(item.MenuID);
          // Set dashboardName if Dashboard (MenuID: 99) is selected
          if (item.MenuID === 99) {
            setDashboardName(item.Name);
          }
        }
        if (item.SubmenuItems.length > 0) {
          selections = [
            ...selections,
            ...initializeSelections(item.SubmenuItems),
          ];
        }
      });
      return [...new Set(selections)];
    };

    const initialSelections = initializeSelections(menuData);
    const currentSelections = getValues(name) || [];

    // Set dashboardName to null if Dashboard is not in selections
    if (!initialSelections.includes(99)) {
      setDashboardName(null);
    }

    // Only set initial values if the field is empty to avoid overwriting user changes
    if (currentSelections.length === 0) {
      setValue(name, initialSelections, { shouldDirty: false });
    }
  }, [menuData, name, setValue, getValues, setDashboardName]);

  const toggleMenu = (menuId: number) => {
    setOpenMenus((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId]
    );
  };

  const getAllChildIds = (menu: MenuItem): number[] => {
    let ids: number[] = [menu.MenuID];
    menu.SubmenuItems.forEach((child) => {
      ids = [...ids, ...getAllChildIds(child)];
    });
    return ids;
  };

  const areAnyChildrenChecked = (
    menu: MenuItem,
    currentSelections: number[]
  ): boolean => {
    if (menu.SubmenuItems.length === 0) {
      return currentSelections.includes(menu.MenuID);
    }
    return menu.SubmenuItems.some((child) =>
      areAnyChildrenChecked(child, currentSelections)
    );
  };

  const handleCheckboxChange = (menu: MenuItem, checked: boolean) => {
    const allIds = getAllChildIds(menu);
    const currentSelections = getValues(name) || [];
    let updatedSelections: number[];

    if (checked) {
      updatedSelections = [...new Set([...currentSelections, ...allIds])];
      if (menu.MenuID === 99) {
        setDashboardName(menu.Name);
      }
    } else {
      updatedSelections = currentSelections.filter(
        (id: number) => !allIds.includes(id)
      );
      if (menu.MenuID === 99) {
        setDashboardName(null);
      }
    }
    setValue(name, updatedSelections, { shouldDirty: true });
  };

  const renderMenuItem = (
    menu: MenuItem,
    level: number = 0,
    isLast: boolean = false
  ) => {
    const hasSubmenu = menu.SubmenuItems.length > 0;
    const isOpen = openMenus.includes(menu.MenuID);
    const isDarkTheme =
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    const lineColor = isDarkTheme ? "#606060" : "#d3d3d3";
    const checkboxBorderColor = isDarkTheme ? "#ffffff" : "#333333";

    return (
      <div
        key={menu.MenuID}
        style={{
          position: "relative",
          paddingLeft: `${level * 20 + 20}px`,
          margin: "5px 0",
        }}
      >
        {level > 0 && (
          <div
            style={{
              position: "absolute",
              top: isLast && !isOpen ? "50%" : "0",
              bottom: isLast && !isOpen ? "50%" : "0",
              left: `${level * 20}px`,
              width: "2px",
              backgroundColor: lineColor,
            }}
          />
        )}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: `${level * 20}px`,
            width: "10px",
            height: "2px",
            backgroundColor: lineColor,
            display: level === 0 ? "none" : "block",
          }}
        />
        <div className="form-check d-flex align-items-center">
          {hasSubmenu && (
            <span
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleMenu(menu.MenuID);
              }}
              style={{
                cursor: "pointer",
                fontSize: "1.2rem",
                marginRight: "8px",
                width: "12px",
                textAlign: "center",
              }}
            >
              <p>{isOpen ? "−" : "+"}</p>
            </span>
          )}
          <Controller
            control={control}
            name={name}
            render={({ field }) => {
              const currentSelections = field.value || [];
              const isChecked = hasSubmenu
                ? areAnyChildrenChecked(menu, currentSelections)
                : currentSelections.includes(menu.MenuID);
              return (
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`menu-${menu.MenuID}`}
                  checked={isChecked}
                  onChange={(e) => {
                    handleCheckboxChange(menu, e.target.checked);
                    const allIds = getAllChildIds(menu);
                    field.onChange(
                      e.target.checked
                        ? [...new Set([...currentSelections, ...allIds])]
                        : currentSelections.filter(
                            (id: number) => !allIds.includes(id)
                          )
                    );
                  }}
                  style={{
                    margin: "0 8px 0 4px",
                    border: `2px solid ${checkboxBorderColor}`,
                    width: "15px",
                    height: "15px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                />
              );
            }}
          />
          <label
            className="form-check-label"
            htmlFor={`menu-${menu.MenuID}`}
            style={{
              fontWeight: level === 0 ? "bold" : "normal",
              userSelect: "none",
            }}
          >
            {menu.Name}
          </label>
        </div>
        {hasSubmenu && isOpen && (
          <div>
            {menu.SubmenuItems.map((submenu, index) =>
              renderMenuItem(
                submenu,
                level + 1,
                index === menu.SubmenuItems.length - 1
              )
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: "1rem" }}>
      <h6>{label}</h6>
      <div style={{ maxHeight: "400px", overflowY: "auto" }}>
        {menuData.map((menu, index) =>
          renderMenuItem(menu, 0, index === menuData.length - 1)
        )}
      </div>
    </div>
  );
};

export default MenuRightsSelector;
